import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client'
import ProductCategories from '@deltek/specification-client/core/Constants/productCategories'
import ProductListingLevels from '@deltek/specification-client/core/Constants/productListingLevels'
import { AppHeaderContext } from '@deltek/specification-client/core/Contexts/AppHeaderContext'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    ListingMatch,
    ListingSearchInput,
    LogEventSource,
} from '@deltek/specification-client/core/Models/listingSearch'
import { SearchFacet } from '@deltek/specification-client/core/Models/searchFacet'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { NormalizedProductType } from '@deltek/specification-client/core/Widgets/ProductListing/model'
import {
    escapeElementDelimiter,
    getInputSelectionValuesFromQueryString,
    getSelectedValuesFromQueryString,
    isUserBPMAdmin,
    sortList,
} from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import { ProductTypeSelectionInput } from '@deltek/specification-client/core/models/productType'
import { SearchFacetInput } from '@deltek/specification-client/core/models/searchFacetInput'
import clsx from 'clsx'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Cell, IdType } from 'react-table'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeGrid } from 'react-window'

import { Reviewer } from '@deltek/specification-client/core/Configurations/Authorization'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { FirmNameQuery } from 'api/graphQL/queries/firm.queries'
import { ListingsGetListingProductTypeProperties } from 'api/graphQL/queries/product.queries'
import { ProductSearchAutosuggestQuery } from 'api/graphQL/queries/productSearch.queries'
import {
    ProductPropertyDetailResponse,
    ProductSearchAutosuggestResponse,
} from 'api/graphQL/types/product.types'
import ProductSearchBar from 'components/Filter/ProductSearchBar'
import { SpecColumn } from 'components/SpecTable'
import SpecTableTitleSettings from 'components/SpecTable/SpecTableTitleSettings'
import { IdTitleProps } from 'components/SpecTable/settings/TableSettings'
import {
    TableSettingsState,
    setCollapseColumnChecked,
    setPinningProps,
    tableSettingsReactiveVar,
} from 'components/SpecTable/settings/tableSettingsReactiveVar'
import { Options as CSVOptions, ExportToCsv } from 'export-to-csv'
import AccordionWindow from 'pages/ProductsPage/ProductListingsPage/ProductListingGroupingTree'
import { ProductListingResultGrid } from 'pages/ProductsPage/ProductListingsPage/components/ProductListingResultGrid'
import { useProductListingStyle } from 'pages/ProductsPage/ProductListingsPage/productListingStyle'
import {
    UserType,
    createFilter,
    getFacetInputsFromFilterSelection,
    getFilterDisabledValues,
    getSelectedProductTypesFromQueryString,
    isAdditionalContentSelected,
    isDeltekUserorAdmin,
    updateQueryString,
} from 'pages/ProductsPage/productsUtils'
import TablePage from 'pages/TablePage'
import ProductHeader from 'pages/components/ProductHeader'
import useGlobalSnackbar from 'shared/hooks/useGlobalSnackbar'
import { columnMap, columns, defaultColumns } from '../../TablePage/tablePage'
import ProductListingLoading from './ProductListingLoading'
import {
    listingPageReactiveVar,
    setHasNextPage,
} from './listingPageReactiveVar'
import {
    CurrentDropDownInput,
    OPTIMISTIC_CARDS,
    PER_PAGE,
    defaultQuery,
    defaultSortOrder,
    defaultfilterParam,
    emptyInputSelectionObject,
    emptySelectionObject,
    facetIdMap,
    validFacets,
    validOrderBy,
} from './productListingPageConstants'
import useListingQueryHelper from './useListingQueryHelper'

const ProductListingsPage: React.FC = () => {
    const infiniteLoaderRef = useRef(null)
    const hasMountedRef = useRef(false)
    const sortRef = useRef<string>('')
    const tableRef = useRef<any>(undefined)
    const accordionTreeRef = useRef<HTMLDivElement>(null)

    const {
        updateAppHeaderContent,
        updateAppHeaderStyles,
        hasLowerBottomToolbar,
    } = useContext(AppHeaderContext)

    const { showGlobalSnackbar } = useGlobalSnackbar()
    const history = useHistory()
    const location = useLocation()

    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )

    const { isDeltekUser, isDeltekAdmin } = useMemo(
        () => isDeltekUserorAdmin(currentUserInfoResponse),
        [currentUserInfoResponse]
    )

    const accountType =
        currentUserInfoResponse?.currentUserInfo.account.accountType
    const accountId = currentUserInfoResponse?.currentUserInfo.account.accountId

    const [listingMatchDataResults, setListingMatchDataResults] = useState<
        Partial<ListingMatch>[] | undefined
    >()
    const [selectedValues, setSelectedValues] = useState<
        Record<string, string[]> | undefined
    >({
        ...emptySelectionObject,
        ...getSelectedValuesFromQueryString(location.search),
    })
    const [selectedInputValues, setSelectedInputValues] = useState<
        Record<string, Date | string | null> | undefined
    >({
        ...emptyInputSelectionObject,
        ...getInputSelectionValuesFromQueryString(location.search),
    })
    const [facets, setFacets] = useState<SearchFacetInput[]>([
        ...getFacetInputsFromFilterSelection(selectedValues),
    ])
    const [currentDropDownName, setCurrentDropDownName] = useState<string>('')
    const [currentDropDownInput, setCurrentDropDownInput] = useState<
        CurrentDropDownInput
    >({ filterName: '', inputValue: '' })

    const [scrollState, setScrollState] = useState({
        rowIndex: 0,
        columnIndex: 0,
    })

    const [columnCount, setColumnCount] = useState<number>(0)
    const [refetch, setRefetch] = useState<boolean>(false)
    // const [hasNextPage, setHasNextPage] = useState<boolean>(true)
    const [productTypeSelections, setProductTypeSelections] = useState<
        NormalizedProductType[]
    >([...getSelectedProductTypesFromQueryString(location.search)])
    const [currentTotal, setCurrentTotal] = useState<number>(PER_PAGE)
    const [searchFetchMoreLoading, setSearchFetchMoreLoading] = useState<
        boolean
    >(false)
    const [tableFilterView, setTableFilterView] = useState<boolean>(true)

    const [defaultExpandedRows, setDefaultExpandedRows] = useState<
        Record<string, boolean> | undefined
    >()
    const [autoResetExpanded, setAutoResetExpanded] = useState(false)

    const classes = useProductListingStyle({ hasLowerBottomToolbar })

    const newDefaultColumns = useMemo(() => {
        return defaultColumns.map((column: SpecColumn<ListingMatch>) => {
            if (column.id === 'productName')
                return {
                    ...column,
                    Cell: ({ row }: Cell<ListingMatch>) => {
                        const listingId = row.original?.listingId
                        const text = !!row.original
                            ? row.original.productName
                                ? row.original.productName
                                : 'No product name'
                            : ''

                        const productDetailPath = !!listingId
                            ? `/${
                                  history.location.pathname.split('/')?.[1]
                              }/product/listing/${listingId}`
                            : '/all'

                        return (
                            <HoverTooltip text={text}>
                                <Link
                                    to={{
                                        pathname: productDetailPath,
                                        search: '?showBackButton=false',
                                    }}
                                    className={classes.productNameText}
                                    target="_blank"
                                >
                                    {text}
                                </Link>
                            </HoverTooltip>
                        )
                    },
                } as SpecColumn<ListingMatch>
            return column
        })
    }, [history])

    const [visibleColumns, setVisibleColumns] = useState([...newDefaultColumns])

    const [hiddenColumns, setHiddenColumns] = useState(
        columns.map((col: SpecColumn<ListingMatch>) => col?.id)
    )

    const [
        isPreviousTableSettingsLoaded,
        setIsPreviousTableSettingsLoaded,
    ] = useState(false)

    const {
        isTableSettingsLoaded,
        searchData,
        isCardViewActive,
        setToggleCardView,
        getListings,
        searchFetchMore,
        listingLoading,
        listingNetworkStatus,
        searchLoading,
        setSearchLoading,
        showBottomLoadingIndicator,
        fetchTableSettings,
        setShowBottomLoadingIndicator,
        setShowTableLoadingIndicator,
        showTableLoadingIndicator,
        saveTableViewSettings,
        setLocalTableSettings,
    } = useListingQueryHelper()

    const { hasNextPage } = useReactiveVar(listingPageReactiveVar)

    const {
        groupColumns: tableGrouping,
        selectedColumns: selectedColumnsState,
        collapseColumnChecked: collapseColumnCheckedState,
        columnWidths: columnWidthsState,
        orderBy: orderByState,
        filters: filtersState,
    } = useReactiveVar<TableSettingsState>(tableSettingsReactiveVar)

    const { memoizedOrderByString } = useMemo(
        () => ({
            memoizedOrderBy: orderByState
                .split(',')
                .reduce((acc: Record<string, boolean>, curr: string) => {
                    const sort = curr.split(' ')
                    acc[sort[0]] = !!sort[1]
                    return acc
                }, {}),
            memoizedOrderByString: orderByState,
        }),
        [orderByState]
    )
    const { memoizedTableGrouping, memoizedGroupIds } = useMemo(
        () => ({
            memoizedTableGrouping: tableGrouping ?? [],
            memoizedGroupIds: (tableGrouping ?? []).map(
                (group: IdTitleProps) => group.id
            ),
        }),
        [tableGrouping]
    )

    const prevOrderBy = usePrevious(memoizedOrderByString)
    const prevFilters = usePrevious(filtersState)

    const [
        manufacturerProductSearchInput,
        setManufacturerProductSearchInput,
    ] = useState<Partial<ListingSearchInput>>({
        skip: 0,
        top: PER_PAGE,
        orderBy: defaultSortOrder,
        logEventSource: LogEventSource.BpmSearchListings,
    })

    const [productCount, setProductCount] = useState(0)

    const [onFilterSelected, setOnFilterSelected] = useState(false)

    const [resultFacetLookup, setResultFacetLookup] = useState<
        Record<string, string[]>
    >({})

    const [filterValues, setFilterValues] = useState<Record<string, string[]>>(
        {}
    )

    const [skip, setSkip] = useState(PER_PAGE)

    const [groupBy, setGroupBy] = useState<Record<string, boolean | string>>({})

    const tableColumns = useMemo(() => [...newDefaultColumns, ...columns], [
        newDefaultColumns,
    ])

    const disabledFilterValues = useMemo(() => {
        const hasManufacturerOptions = !!selectedValues?.withManufacturerOption?.some(
            (value) => value === 'true'
        )
        return getFilterDisabledValues(
            resultFacetLookup,
            hasManufacturerOptions
        )
    }, [selectedValues, resultFacetLookup, getFilterDisabledValues])

    const sortedProductList = useMemo(() => {
        return sortList(
            [...(listingMatchDataResults ?? [])],
            memoizedOrderByString
        )
    }, [listingMatchDataResults, memoizedOrderByString, visibleColumns])

    //#region graphql mutation and queries
    const [getFirmNameInfo, { data: currentFirmNameInfo }] = useLazyQuery<any>(
        FirmNameQuery,
        { fetchPolicy: 'no-cache', nextFetchPolicy: 'no-cache' }
    )

    const { loading: autosuggestLoading, data: autosuggestData } = useQuery<
        ProductSearchAutosuggestResponse
    >(ProductSearchAutosuggestQuery, {
        variables: {
            input: {
                facets: createFilter(
                    facets,
                    'AutoSuggest',
                    currentDropDownName
                ).filter(
                    (facet) => facet.name !== currentDropDownName
                    // don't limit autosuggest by what's already selected in same filter
                ),
                searchLimit: 50,
                searchField: currentDropDownName,
                searchText: currentDropDownInput.inputValue,
                productTypeSelections:
                    currentDropDownName !== 'sectionNameWithProductType'
                        ? productTypeSelections.map(
                              (each) =>
                                  ({
                                      productTypeProperties:
                                          each.productTypeProperties,
                                      sectionName: each.sectionName,
                                      productTypeName: each.productTypeName,
                                  } as ProductTypeSelectionInput)
                          )
                        : [],
            },
        },
        skip: !currentDropDownName, // only run query if an autosuggest drop-down is open
        fetchPolicy: 'no-cache',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product autosuggest',
                    variant: 'error',
                    id: `ProductListingsPage_ProductAutosuggestError`,
                },
            })
        },
    })

    const [
        getListingProductTypeProperties,
        {
            loading: loadingSelectedProductType,
            error: errorSelectedProductType,
            data: dataSelectedProductType,
        },
    ] = useLazyQuery<ProductPropertyDetailResponse>(
        ListingsGetListingProductTypeProperties,
        {
            displayName: 'GetListingProductTypeProperties',
            onError: (error) => {
                showGlobalSnackbar?.({
                    variables: {
                        message: summarizeApolloError(
                            error,
                            'Error fetching product type properties.'
                        ),
                        variant: 'error',
                        id:
                            'ProductTypeFilter_ListingProductTypePropertiesError',
                    },
                })
            },
            onCompleted: () => setOnFilterSelected(true),
        }
    )
    //#endregion

    const isItemLoaded = (index: number) => !!listingMatchDataResults?.[index]

    //#endregion CardView_Callback_Functions
    const setScrollRowAndColum = useCallback(
        (rowIndex: number, columnIndex: number) => {
            setScrollState({ rowIndex, columnIndex })
        },
        []
    )

    const handleAutoSuggestDropdown = (value: string) => {
        setCurrentDropDownName(value)
        setFilterValues((past) => ({
            ...past,
            sectionNames: [],
            sectionNameWithProductType: [],
            displayNameWithFirmName: [],
        }))
    }

    const handleClearAll = () => {
        const search = `?${defaultQuery}`
        history.replace({
            ...history.location,
            search,
        })
        setSelectedInputValues({
            ...emptyInputSelectionObject,
            ...getInputSelectionValuesFromQueryString(search),
        })
        setProductTypeSelections([
            ...getSelectedProductTypesFromQueryString(search),
        ])
        const updatedSelectedValues = {
            ...emptySelectionObject,
            ...getSelectedValuesFromQueryString(search),
        }
        setSelectedValues(updatedSelectedValues)
        setFacets([...getFacetInputsFromFilterSelection(updatedSelectedValues)])
    }

    const onFilterSelectionChangeHandler = useCallback(
        (selection: Record<string, string[]> | undefined) => {
            const selectedData: Record<string, string[]> = {
                ...selection,
            }

            if (
                selectedData.orderBy?.length === 2 &&
                !isEqual(selectedData.orderBy, selectedValues?.orderBy)
            ) {
                const groupByDataObj = JSON.parse(selectedData.orderBy[0])
                const sortByDataObj = JSON.parse(selectedData.orderBy[1])
                const orderByData = [...sortByDataObj].filter(
                    (fieldName) => !!fieldName
                ) as string[]
                setGroupBy(groupByDataObj)
                selectedData.orderBy = orderByData
            } else if (
                selectedData.orderBy?.length !== 2 &&
                Object.keys(groupBy).length !== 0
            )
                setGroupBy({})
            else if (Object.values(groupBy).includes('spLoadMore'))
                setGroupBy({ ...lastGroupingInfoRef.current })
            setSelectedValues(selectedData)
            setFacets(getFacetInputsFromFilterSelection(selectedData))
            updateQueryString(selectedData, undefined, history, location)
            setAutoResetExpanded(false)
        },
        [groupBy, history, location, selectedValues]
    )

    const handleFilterInputChangeDebounced = useCallback(
        (filterName, inputValue) => {
            setCurrentDropDownInput({
                filterName,
                inputValue,
            })
        },
        []
    )

    const handleEmptyInputSelection = useCallback(
        (selection) => {
            setSelectedInputValues(selection)
            updateQueryString(undefined, selection, history, location)
        },
        [history, location]
    )

    const handleProductTypeSelection = useCallback(
        (values: NormalizedProductType[]) => {
            const propArr: string[] = []
            values.forEach((prop) => {
                if (prop.productTypeProperties) {
                    prop.productTypeProperties?.forEach(
                        (ptp: NormalizedProductType) =>
                            propArr.push(
                                `${prop.productTypeName}%|%${
                                    ptp.propertyName
                                }:${ptp.propertyOptions?.join(',')}`
                            )
                    )
                } else {
                    propArr.push(
                        `${prop.sectionName}%|%${prop.productTypeName}`
                    )
                }
            })

            const productTypePropertiesQuery =
                (propArr.length && encodeURIComponent(propArr.join(';'))) || ''
            const searchParams = new URLSearchParams(location.search)
            searchParams.has('ptp') && searchParams.delete('ptp')
            if (productTypePropertiesQuery) {
                searchParams.set('ptp', productTypePropertiesQuery)
            }
            if (values.length > 0 && searchParams.has('sp')) {
                history.replace({
                    ...location,
                    search: searchParams.toString(),
                })
            }
            if (!isEqual(values, productTypeSelections))
                setProductTypeSelections(values)
        },
        [history, location, productTypeSelections]
    )

    const handleCardViewFilterClick = useCallback((showFilters: boolean) => {
        setTableFilterView(showFilters)
    }, [])

    const handleViewMenuChange = useCallback(
        (event: React.ChangeEvent<{ value: unknown }>) => {
            let defaultPath = `/products/${event.target.value}`
            if (event.target.value === 'all')
                defaultPath = `/products/${event.target.value}${location.search}`
            history.push(defaultPath)
        },
        [history, location.search]
    )

    const handleCompanyRoute = useCallback(() => {
        history.push(
            `/products/company/${currentUserInfoResponse?.currentUserInfo.account.accountId}`
        )
    }, [currentUserInfoResponse, history])
    //#endregion CardView_Callback_Functions

    //#region Table_Callback_Functions
    const getExpandedRows = useCallback(
        (
            listings: Partial<ListingMatch>[] | undefined,
            collapseGroupState: Record<string, boolean>
        ) => {
            const expandedRows: Record<string, boolean> = {}
            if (!!collapseGroupState) {
                const collapsedStates = Object.entries(collapseGroupState)
                listings?.forEach((listing) => {
                    for (var i = 0; i < collapsedStates.length; i++) {
                        const [firstGroupedColumnId, value] = collapsedStates[0]
                        let key: string
                        if (i === 0) {
                            let key = `${firstGroupedColumnId}:${
                                listing[
                                    firstGroupedColumnId as keyof ListingMatch
                                ]
                            }`
                            expandedRows[key] = !value
                        }

                        if (i === 1) {
                            const [
                                secondGroupedColumnId,
                                value,
                            ] = collapsedStates[i]
                            key = `${firstGroupedColumnId}:${
                                listing[
                                    firstGroupedColumnId as keyof ListingMatch
                                ]
                            }>${secondGroupedColumnId}:${
                                listing[
                                    secondGroupedColumnId as keyof ListingMatch
                                ]
                            }`
                            expandedRows[key] = !value
                        }
                        if (i === 2) {
                            const [secondGroupedColumnId] = collapsedStates[1]
                            const [
                                thirdGroupedColumnId,
                                value,
                            ] = collapsedStates[i]
                            key = `${firstGroupedColumnId}:${
                                listing[
                                    firstGroupedColumnId as keyof ListingMatch
                                ]
                            }>${secondGroupedColumnId}:${
                                listing[
                                    secondGroupedColumnId as keyof ListingMatch
                                ]
                            }>${thirdGroupedColumnId}:${
                                listing[
                                    thirdGroupedColumnId as keyof ListingMatch
                                ]
                            }`
                            expandedRows[key] = !value
                        }
                    }
                })
            }
            return expandedRows
        },
        []
    )

    const handleTableOrder = useCallback(
        (columnOrder: IdType<any>[]) => {
            const newColumns = tableColumns
                ?.filter((col) =>
                    columnOrder?.find((newCol) => newCol === col.id)
                )
                .map((column) => {
                    return {
                        ...column,
                        width: columnWidthsState?.[column.id ?? ''] ?? 300,
                        index: columnOrder?.findIndex(
                            (col) => col === column.id
                        ),
                    }
                })
                .sort((a, b) => a.index - b.index)
            setVisibleColumns(newColumns)
        },
        [tableColumns, columnWidthsState]
    )

    const handleColumnRemove = useCallback((hiddenColumns: IdType<any>[]) => {
        setHiddenColumns(hiddenColumns as string[])
    }, [])

    const handleCollapseColumnState = useCallback(
        (collapseColumnState: Record<string, boolean> | undefined) => {
            if (!!collapseColumnState) {
                setCollapseColumnChecked(collapseColumnState)
                const expandedRows = getExpandedRows(
                    listingMatchDataResults,
                    collapseColumnState
                )
                tableRef.current.handleToggleRowExpanded(expandedRows)
            }

            setAutoResetExpanded(false)
        },
        [listingMatchDataResults]
    )
    //#endregion Table_Callback_Functions

    //#region Common_Callback_Functions
    const doRefetch = useCallback(() => {
        setShowTableLoadingIndicator(true)
        setIsPreviousTableSettingsLoaded(false)
        getListings({
            variables: {
                manufacturerProductSearchInput: {
                    ...manufacturerProductSearchInput,
                    facets: manufacturerProductSearchInput?.facets,
                },
            },
        })
        setListingMatchDataResults([])
        setRefetch(false)
        setSearchLoading(true)
    }, [getListings, manufacturerProductSearchInput])

    const handleLoadMore = useCallback(
        (_startIndex: number, stopIndex: number) => {
            if (
                !searchLoading &&
                !searchFetchMoreLoading &&
                !refetch &&
                searchData?.listingSearch?.count &&
                Math.ceil(currentTotal / columnCount) - stopIndex > 0 &&
                hasNextPage &&
                !!searchFetchMore
            ) {
                setSearchFetchMoreLoading(true)
                setCurrentTotal((prev) => prev + OPTIMISTIC_CARDS)

                return searchFetchMore({
                    variables: {
                        manufacturerProductSearchInput: {
                            ...manufacturerProductSearchInput,
                            skip: searchData.listingSearch.matches?.length,
                            top: PER_PAGE,
                        },
                    },
                }).then(() => setSearchFetchMoreLoading(false))
            }
            return null
        },
        [
            manufacturerProductSearchInput,
            searchLoading,
            searchFetchMoreLoading,
            refetch,
            searchData,
            currentTotal,
            columnCount,
            hasNextPage,
            searchFetchMore,
        ]
    )
    const setGroupFromUrl = useCallback(() => {
        const searchParams = new URLSearchParams(location.search)
        if (
            Object.keys(groupBy).length === 0 &&
            location.search &&
            (searchParams.has('groupby') || searchParams.has('sortby'))
        ) {
            const groupByData =
                (searchParams.has('groupby') &&
                    decodeURIComponent(searchParams.get('groupby')!)) ||
                ''
            const sortBy =
                (searchParams.has('sortby') &&
                    decodeURIComponent(searchParams.get('sortby')!)) ||
                ''
            let groupByObj: Record<string, boolean> = {}
            groupByData
                ?.split(',')
                .forEach(
                    (group) =>
                        group?.split(' ')[0] &&
                        (groupByObj[group?.split(' ')[0]] =
                            group?.split(' ')[1] === 'true')
                )
            if (sortRef.current === sortBy && isEqual(groupByObj, groupBy))
                return
            if (!isEqual(groupByObj, groupBy)) {
                setGroupBy(groupByObj)
            }
            const currentOrderby = ((sortBy && sortBy.split(',')) || []).filter(
                (sort) => !!sort
            )
            if (!isEqual(selectedValues?.orderBy, currentOrderby)) {
                setSelectedValues({
                    ...selectedValues,
                    orderBy: currentOrderby,
                })
            }
            sortRef.current = sortBy
        }
    }, [location, selectedValues, groupBy])

    const handleViewToggle = useCallback(() => {
        setToggleCardView(!isCardViewActive)
        // TODO: Product Card Management Table : Set Product Card List settings here.
        // setSortingGrouping({
        //     sorting: orderByState,
        //     collapseColumnChecked: collapseColumnCheckedState,
        // })
    }, [
        isCardViewActive,
        orderByState,
        collapseColumnCheckedState,
        setToggleCardView,
    ])

    const handlePrintClick = useCallback(() => {
        window.print()
    }, [])
    const handleExportClick = useCallback(() => {
        if (!sortedProductList) return
        const columns = [...tableGrouping, ...selectedColumnsState] ?? []
        const header = columns.map((col) => col.title) ?? []
        const colIds = columns.map((col) => col.id) ?? []

        const fileName = 'ProductCardManagementTable - Specpoint'
        const options: CSVOptions = {
            filename: fileName,
            fieldSeparator: ',',
            showLabels: true,
            headers: header,
        }
        const csvExporter = new ExportToCsv(options)

        const data = sortedProductList.map((row) =>
            colIds.map((keyName) => {
                if (Array.isArray(row[keyName]) && keyName === 'categories') {
                    return `${(row[keyName] as string[])
                        ?.join(', ')
                        .replaceAll("'", '')
                        .trim()}`
                } else if (keyName === 'isActive') {
                    return row[keyName] ? 'Active' : 'Inactive'
                } else if (keyName === 'displayNameWithFirmName') {
                    const actualString = row[keyName]
                        ?.split('%|%')
                        ?.map((val: string | null) => val?.replaceAll("'", ''))
                    const formattedString =
                        actualString?.[0] !== 'null'
                            ? `${actualString?.[0]} (${actualString?.[1]})`
                            : `${actualString?.[1]}`
                    return formattedString?.trim()
                } else if (keyName === 'productName') {
                    return !!row[keyName] ? row[keyName] : 'No product name'
                } else if (keyName === 'specAgentProductTypeId') {
                    const actualString = String(row[keyName])
                    return actualString
                }
                return row[keyName] ?? ''
            })
        )
        csvExporter.generateCsv(data)
    }, [sortedProductList, selectedColumnsState, tableGrouping])
    //#endregion Common_Callback_Functions

    //#region useEffects
    useEffect(() => {
        const { isBPM } = isUserBPMAdmin(currentUserInfoResponse)
        const searchParams = new URLSearchParams(history.location.search)

        if (!isBPM || !searchParams?.has('defaultFilter')) return
        if (
            !isBPM ||
            searchParams.has(defaultfilterParam) ||
            !searchParams?.has('defaultFilter') ||
            !currentFirmNameInfo?.firm?.name
        )
            return
        const assignedCompany = escapeElementDelimiter(
            `'${currentFirmNameInfo?.firm?.displayName}'%|%'${currentFirmNameInfo?.firm?.name}'`
        )
        searchParams.append(defaultfilterParam, assignedCompany)
        searchParams.delete('defaultFilter')
        history.replace({
            ...history.location,
            search: `?${searchParams.toString()}`,
        })
        const updatedSelectedValues = {
            ...selectedValues,
            ...getSelectedValuesFromQueryString(searchParams.toString()),
        }
        if (
            isEqual(
                facets,
                getFacetInputsFromFilterSelection(updatedSelectedValues)
            )
        )
            return
        setSelectedValues(updatedSelectedValues)
        setFacets([...getFacetInputsFromFilterSelection(updatedSelectedValues)])
    }, [
        currentFirmNameInfo,
        selectedValues,
        history,
        facets,
        currentUserInfoResponse,
    ])

    useEffect(() => {
        const searchFacets = createFacetLookup(
            searchData?.listingSearch.facets ?? []
        )

        if (searchData?.listingSearch.matches) {
            setProductCount(searchData?.listingSearch.count ?? 0)
            setResultFacetLookup(searchFacets)

            const listingMatchData =
                isCardViewActive ||
                !(!isCardViewActive && listingMatchDataResults?.length! < skip)
                    ? [...searchData?.listingSearch?.matches]
                    : searchData?.listingSearch?.matches?.length! ===
                          searchData?.listingSearch?.count! &&
                      searchData?.listingSearch?.count! < skip
                    ? [...searchData?.listingSearch?.matches]
                    : (Array.from(
                          new Set([
                              ...(listingMatchDataResults as Partial<
                                  ListingMatch
                              >[]),
                              ...searchData?.listingSearch?.matches,
                          ])
                      ) as Partial<ListingMatch>[])

            setListingMatchDataResults(listingMatchData)
        }
    }, [searchData, location, selectedValues, isCardViewActive, skip])

    useEffect(() => {
        updateAppHeaderStyles?.({
            titleTextColor: '#04325B',
            backgroundColor: '#ffffff',
        })
    }, [updateAppHeaderStyles])

    useEffect(() => {
        if (currentUserInfoResponse) {
            if (searchData)
                updateAppHeaderContent(
                    `Product Card Management (${productCount ?? 0})`,
                    <ProductHeader
                        roles={{
                            isDeltekAdmin,
                        }}
                        firmId={
                            currentUserInfoResponse.currentUserInfo.account
                                .accountId
                        }
                        showAddProductButton={isDeltekAdmin}
                        loading={false}
                        onMenuChange={handleViewMenuChange}
                        onCompanyButtonClick={handleCompanyRoute}
                    />
                )
        }
    }, [
        updateAppHeaderContent,
        searchData,
        currentUserInfoResponse,
        searchLoading,
        productCount,
        handleViewMenuChange,
        handleCompanyRoute,
        isDeltekAdmin,
    ])

    useEffect(() => {
        if (!autosuggestLoading) {
            const autosuggestValues =
                autosuggestData?.listingAutoSuggest?.result || []
            setFilterValues({
                categories:
                    resultFacetLookup?.Categories?.sort() ?? ProductCategories,
                listingLevels: ProductListingLevels,
                displayNameWithFirmName:
                    currentDropDownName === 'displayNameWithFirmName'
                        ? autosuggestValues
                        : [],
                sectionNames:
                    currentDropDownName === 'sectionNames'
                        ? autosuggestValues
                        : [],
                sectionNameWithProductType:
                    currentDropDownName === 'sectionNameWithProductType'
                        ? autosuggestValues
                        : [],
            })
        }
    }, [
        autosuggestData,
        autosuggestLoading,
        currentDropDownName,
        resultFacetLookup,
    ])

    useEffect(() => {
        const { isBPM } = isUserBPMAdmin(currentUserInfoResponse)
        if (!currentFirmNameInfo && accountId && isBPM)
            getFirmNameInfo({
                variables: {
                    firmId: accountId,
                },
            })
    }, [
        currentUserInfoResponse,
        currentFirmNameInfo,
        getFirmNameInfo,
        location,
        accountId,
    ])

    // Used to reset the scrollbar
    useEffect(() => {
        if (hasMountedRef.current) {
            if (infiniteLoaderRef) {
                const list = infiniteLoaderRef.current as any
                if (list) {
                    const grid = list._listRef as FixedSizeGrid
                    list.resetloadMoreItemsCache() //Reset infinite scroll
                    grid.scrollToItem({
                        align: 'start',
                        columnIndex: 0,
                        rowIndex: 0,
                    }) //scroll to top after refresh
                    list.scrollTop = 0 //firefox solution for scroll not reseting
                }
            }
        }
        hasMountedRef.current = true
    }, [refetch, searchLoading])

    // Update current total based on cache
    useEffect(() => {
        if (
            !searchLoading &&
            !searchFetchMoreLoading &&
            !refetch &&
            !!searchData?.listingSearch?.matches &&
            !!productCount
        ) {
            const count =
                searchData?.listingSearch?.matches?.length < PER_PAGE
                    ? productCount
                    : searchData?.listingSearch?.matches?.length

            if (currentTotal !== count && searchData?.listingSearch?.count) {
                setCurrentTotal(count)

                setHasNextPage(
                    count >= PER_PAGE
                        ? count < searchData?.listingSearch?.count
                        : false
                )
            }
        }
    }, [
        searchData,
        searchLoading,
        searchFetchMoreLoading,
        currentTotal,
        refetch,
        productCount,
    ])

    useEffect(() => {
        setGroupFromUrl()
    }, [setGroupFromUrl])

    const lastGroupingInfoRef = useRef<Record<string, boolean | string>>()

    const handleLoadMoreResults = useCallback(() => {
        const nextSkip = skip + PER_PAGE
        setSearchLoading(true)
        setShowTableLoadingIndicator(false)
        setShowBottomLoadingIndicator(true)
        getListings &&
            getListings({
                variables: {
                    manufacturerProductSearchInput: {
                        ...manufacturerProductSearchInput,
                        facets: manufacturerProductSearchInput.facets,
                        skip,
                        top: PER_PAGE,
                    },
                },
            })

        setSkip(nextSkip)
    }, [getListings, skip, manufacturerProductSearchInput, groupBy])

    useEffect(() => {
        if (
            !searchLoading &&
            accordionTreeRef &&
            isCardViewActive &&
            listingMatchDataResults?.length
        ) {
            const accordionTree = accordionTreeRef?.current
            const accordionTreeChild = accordionTree?.firstElementChild as HTMLElement

            if (accordionTreeChild) {
                accordionTreeChild.setAttribute('tabIndex', '0')
                accordionTreeChild.focus()
            }
        }
    }, [
        searchLoading,
        accordionTreeRef.current,
        isCardViewActive,
        listingMatchDataResults,
    ])

    // Add the Group columns to the visible columns
    useEffect(() => {
        const groupingColumn = visibleColumns?.filter((col) =>
            memoizedTableGrouping?.find(
                (grouping: IdTitleProps) => grouping.id === col.id
            )
        )

        if (groupingColumn?.length !== memoizedTableGrouping?.length) {
            const columnsToAdd = memoizedTableGrouping.map(
                (grouping: IdTitleProps) => {
                    return tableColumns?.find((col) => col.id === grouping.id)
                }
            ) as SpecColumn<ListingMatch>[]

            setVisibleColumns((past) => [...past, ...columnsToAdd])
        }
    }, [visibleColumns, memoizedTableGrouping, tableColumns])

    //#endregion

    // This is for the CardView
    useMemo(() => {
        let computedFacets = createFilter(facets, 'Search')

        // all AE users and reviewers except Deltek (system) MUST always force active only listings
        if (
            accountType === UserType.AEfirm ||
            (accountType !== UserType.Deltek &&
                currentUserInfoResponse?.currentUserInfo.roles.includes(
                    Reviewer
                ))
        ) {
            // check if isActive is already included,
            // if true, remove it first so we don't have to worry about the value
            // then push the correct isActive = true.
            computedFacets.filter((facet) => facet['name'] !== 'isActive')

            computedFacets.push({
                name: 'isActive',
                values: ['true'],
            })
        }

        setManufacturerProductSearchInput((past) => ({
            ...past,
            orderBy: selectedValues?.orderBy?.length
                ? selectedValues.orderBy.join(',')
                : defaultSortOrder,
            facets: computedFacets,
            lastUpdatedEndDate: selectedInputValues?.lastUpdatedEndDate
                ? selectedInputValues.lastUpdatedEndDate + 'T23:59:59.999Z'
                : undefined,
            lastUpdatedStartDate: selectedInputValues?.lastUpdatedStartDate
                ? selectedInputValues.lastUpdatedStartDate + 'T00:00:00.000Z'
                : undefined,
            lastUpdatedInPastDays: selectedInputValues?.lastUpdatedInPastDays
                ? 180
                : undefined,
            hasBimLinks: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasBimLinks'
                  )
                : undefined,
            hasCatalogs: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasCatalogs'
                  )
                : undefined,
            hasDocumentFL: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasDocumentFL'
                  )
                : undefined,
            hasDocumentSF: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasDocumentSF'
                  )
                : undefined,
            productTypeSelections: productTypeSelections.map(
                (each) =>
                    ({
                        productTypeProperties: each.productTypeProperties,
                        sectionName: each.sectionName,
                        productTypeName: each.productTypeName,
                    } as ProductTypeSelectionInput)
            ),
        }))
        setSkip(PER_PAGE)
        setRefetch(true)
        setCurrentTotal(10)
    }, [
        facets,
        selectedValues,
        productTypeSelections,
        selectedInputValues,
        currentUserInfoResponse,
        accountType,
    ])

    const createFacetLookup = (dataFacets: Partial<SearchFacet>[]) => {
        return dataFacets.reduce((result, facet) => {
            result[facet.name ?? ''] =
                facet?.values?.map((fv) => fv.value || '') || []

            return result
        }, {} as Record<string, string[]>)
    }

    // Useeffect for calling the API when the filters change
    useEffect(() => {
        if (
            isTableSettingsLoaded &&
            isPreviousTableSettingsLoaded &&
            (!isEqual(prevFilters, filtersState) ||
                !isEqual(prevOrderBy, orderByState))
        ) {
            const orderByStateArray = [
                ...new Set(
                    orderByState
                        .replace('sectionName', 'sectionNumber')
                        .replace('familyNumber', 'sectionNumber')
                        .split(',')
                ),
            ]

            const validOrderByFiltered = orderByStateArray.filter(
                (orderBy: string) => {
                    return validOrderBy.includes(orderBy.split(' ')[0])
                }
            )

            const validFacetsFiltered = filtersState.filter((filter: any) => {
                if (
                    filter.id === 'hasManufacturerOption' || // hasManufacturerOption is withManufacturerOption
                    filter.id === 'type' // type is listingLevel
                ) {
                    return true
                }
                return validFacets.includes(filter.id)
            })

            // Verify if we need to filter by partnerships boolean or text search
            // if(filtersState.some((filter: any) => filter.id === 'partnershipLinksSM' || filter.id === 'partnershipLinksSM')) {
            //     validFacetsFiltered.push({
            //         id: 'partnerships',
            //         value: filtersState.filter((filter: any) => filter.id === 'partnershipLinksSM' || filter.id === 'partnershipLinksSM').map((filter: any) => filter.value).flat()
            //     })
            // }

            setManufacturerProductSearchInput((past) => {
                const newFacets = {
                    ...past,
                    facets: validFacetsFiltered.map((facet) => {
                        let facetValue = facet.value

                        if (
                            facet.id === 'partnershipLinksSM' ||
                            facet.id === 'partnershipLinksSM'
                        ) {
                        }

                        if (facet.id === 'isActive') {
                            facetValue = facet.value.map((value: string) =>
                                value === 'Inactive' ? 'false' : 'true'
                            )
                        }

                        return {
                            name: facetIdMap[facet.id],
                            values: facetValue,
                        }
                    }),
                    orderBy: validOrderByFiltered.join(','),
                }

                if (!isEqual(newFacets, past)) {
                    setRefetch(true)
                    return newFacets
                }
                return past
            })
        }
    }, [
        filtersState,
        orderByState,
        prevOrderBy,
        prevFilters,
        isTableSettingsLoaded,
        isPreviousTableSettingsLoaded,
    ])

    useEffect(() => {
        if (
            isTableSettingsLoaded &&
            !isPreviousTableSettingsLoaded &&
            [...selectedColumnsState, ...tableGrouping].length > 0 &&
            !!listingMatchDataResults?.length
        ) {
            setVisibleColumns(
                [
                    ...selectedColumnsState.map((col) => {
                        return {
                            ...(columnMap[col.id] as SpecColumn<ListingMatch>),
                            width: columnWidthsState?.[col.id] ?? 300,
                        }
                    }),
                ].map((column: SpecColumn<ListingMatch>) => {
                    if (column.id === 'productName')
                        return {
                            ...column,
                            Cell: ({ row }: Cell<ListingMatch>) => {
                                const listingId = row.original?.listingId
                                const text = !!row.original
                                    ? row.original.productName
                                        ? row.original.productName
                                        : 'No product name'
                                    : ''

                                const productDetailPath = !!listingId
                                    ? `/${
                                          history.location.pathname.split(
                                              '/'
                                          )?.[1]
                                      }/product/listing/${listingId}`
                                    : '/all'

                                return (
                                    <HoverTooltip text={text}>
                                        <Link
                                            to={{
                                                pathname: productDetailPath,
                                                search: '?showBackButton=false',
                                            }}
                                            className={classes.productNameText}
                                            target="_blank"
                                        >
                                            {text}
                                        </Link>
                                    </HoverTooltip>
                                )
                            },
                        } as SpecColumn<ListingMatch>
                    return column
                })
            )

            setIsPreviousTableSettingsLoaded(true)
            setHiddenColumns([
                ...Object.keys(columnMap).filter(
                    (col) =>
                        !selectedColumnsState.map((col) => col.id).includes(col)
                ),
            ])
        }
    }, [
        collapseColumnCheckedState,
        history,
        listingMatchDataResults,
        isTableSettingsLoaded,
        isPreviousTableSettingsLoaded,
        selectedColumnsState,
        columnWidthsState,
        tableGrouping,
        orderByState,
        searchData,
    ])

    //on refetch change

    useEffect(() => {
        const expandedRows = getExpandedRows(
            listingMatchDataResults,
            tableGrouping.reduce((acc: Record<string, boolean>, curr) => {
                acc[curr.id] = collapseColumnCheckedState[curr.id]
                return acc
            }, {})
        )

        setDefaultExpandedRows(expandedRows)
        setAutoResetExpanded(true)
    }, [
        listingMatchDataResults,
        collapseColumnCheckedState,
        getExpandedRows,
        tableGrouping,
    ])

    useEffect(() => {
        if (refetch) {
            doRefetch()
            setHasNextPage(false)
        }
    }, [doRefetch, refetch])

    useEffect(() => {
        return () => {
            setLocalTableSettings()
        }
    }, [location])

    // onRender
    useEffect(() => {
        doRefetch()
        fetchTableSettings()
        const saveTableSettingsInterval = setInterval(() => {
            saveTableViewSettings()
        }, 1000 * 60)

        // save table settings on page change
        return () => {
            clearInterval(saveTableSettingsInterval)
        }
    }, [])

    // window event listeners
    useEffect(() => {
        window.addEventListener('beforeunload', setLocalTableSettings)
        return () => {
            window.removeEventListener('beforeunload', setLocalTableSettings)
        }
    }, [])

    return (
        <GridContainer className={classes.container} direction="column">
            <GridItem
                className={classes.productSearchContOuter}
                data-testid="productListingsPage"
            >
                <ProductSearchBar
                    filterValues={filterValues}
                    filterSelection={selectedValues}
                    filterDisabledValues={disabledFilterValues}
                    loadingFilterName={
                        autosuggestLoading ? currentDropDownName : ''
                    }
                    currentUserInfoResponse={currentUserInfoResponse}
                    emptyInputSelection={selectedInputValues}
                    productTypeSelections={productTypeSelections}
                    showGlobalSnackbar={showGlobalSnackbar}
                    resultCount={productCount ?? 0}
                    loadingSelectedProductType={loadingSelectedProductType}
                    errorSelectedProductType={errorSelectedProductType}
                    dataSelectedProductType={dataSelectedProductType}
                    getListingProductTypeProperties={
                        getListingProductTypeProperties
                    }
                    showProductSortingModal={isCardViewActive}
                    onSelected={onFilterSelected}
                    setOnSelected={setOnFilterSelected}
                    clearAllHandler={handleClearAll}
                    onFilterSelectionChange={onFilterSelectionChangeHandler}
                    onFilterInputChangeDebounced={
                        handleFilterInputChangeDebounced
                    }
                    handelProductTypeSelection={handleProductTypeSelection}
                    handleAutoSuggestDropdown={handleAutoSuggestDropdown}
                    handleEmptyInputSelection={handleEmptyInputSelection}
                />
            </GridItem>
            <GridItem className={classes.specTableContainer}>
                <SpecTableTitleSettings
                    tableViewActive={!isCardViewActive}
                    showFiltersRow={!isCardViewActive && tableFilterView}
                    showTableSettings={!isCardViewActive}
                    tableSettings={{
                        maxGroupCount: 3,
                        showGroupOptionSetting: true,
                        showPinning: true,
                        showCollapseToggle: true,
                        columns: tableColumns as any,
                        visibleColumns: visibleColumns as any,
                        initialHiddenColumnIds: hiddenColumns as string[],
                        onTableSettingsChange: setPinningProps,
                        setColumnOrder: handleTableOrder,
                        setHiddenColumns: handleColumnRemove,
                        setCollapsedGroupColumns: handleCollapseColumnState,
                    }}
                    theme="light"
                    onFilterClick={handleCardViewFilterClick}
                    onPrintClick={handlePrintClick}
                    onExportClick={handleExportClick}
                    onTableViewClick={handleViewToggle}
                    onCardViewClick={handleViewToggle}
                />
            </GridItem>
            <GridItem className={classes.productContent}>
                {isCardViewActive ? (
                    <>
                        {!Object.keys(groupBy).length ? (
                            <ProductListingResultGrid
                                searchLoading={searchLoading}
                                searchFetchMoreLoading={searchFetchMoreLoading}
                                products={listingMatchDataResults}
                                currentTotal={currentTotal}
                                hasNextPage={hasNextPage}
                                isItemLoaded={isItemLoaded}
                                scrollState={scrollState}
                                setScrollRowAndColumn={setScrollRowAndColum}
                                infiniteLoaderRef={infiniteLoaderRef}
                                setColumnCount={setColumnCount}
                                handleLoadMore={handleLoadMore}
                            />
                        ) : !searchLoading &&
                          listingMatchDataResults?.length === 0 ? (
                            <GridContainer
                                spacing={1}
                                className={clsx(
                                    'divProductListingNoResultGrid',
                                    classes.resultContainer
                                )}
                                justify="center"
                                alignContent="space-between"
                            >
                                <GridItem>
                                    <h3>No Results</h3>
                                </GridItem>
                            </GridContainer>
                        ) : (
                            <AutoSizer
                                className="accordionTree"
                                data-testid="accordion-tree"
                            >
                                {({ height, width }) => (
                                    <AccordionWindow
                                        accordionTreeRef={accordionTreeRef}
                                        listingCount={productCount}
                                        listings={sortedProductList}
                                        searchLoading={searchLoading}
                                        groupBy={groupBy}
                                        height={height}
                                        width={width}
                                        listingLoading={listingLoading}
                                        onLoadMore={handleLoadMoreResults}
                                        isDeltekUser={isDeltekUser}
                                    />
                                )}
                            </AutoSizer>
                        )}
                    </>
                ) : (
                    <TablePage
                        tableData={sortedProductList}
                        tableDataNetworkStatus={listingNetworkStatus}
                        totalItems={searchData?.listingSearch?.count ?? 0}
                        showFiltersRow={tableFilterView}
                        columns={visibleColumns}
                        hiddenColumns={hiddenColumns as string[]}
                        groupBy={memoizedGroupIds}
                        fetchMoreTableData={handleLoadMoreResults}
                        defaultExpandedRows={defaultExpandedRows}
                        setAutoResetExpanded={setAutoResetExpanded}
                        autoResetExpanded={autoResetExpanded}
                        tableRef={tableRef}
                        fetchDataLoading={
                            showTableLoadingIndicator || listingLoading
                        }
                        fetchNextDataLoading={listingLoading}
                        showBottomLoadingIndicator={showBottomLoadingIndicator}
                        setFacets={setFacets}
                    ></TablePage>
                )}
            </GridItem>
            <ProductListingLoading />
        </GridContainer>
    )
}

export default ProductListingsPage
